import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CONTACT_INFO, WHATSAPP_MESSAGES } from '../data/mockData';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Hi! 👋 I\'m the Rocket Van Express assistant. I can help you with:\n\n• Getting a quick quote\n• Checking service areas\n• Understanding our pricing\n• Answering FAQs\n\nHow can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickReplies = [
    { text: 'Get a Quote', action: 'quote' },
    { text: 'Service Areas', action: 'areas' },
    { text: 'Pricing Info', action: 'pricing' },
    { text: 'Talk to Human', action: 'whatsapp' }
  ];

  const handleQuickReply = (action) => {
    let response = '';
    
    switch(action) {
      case 'quote':
        response = 'Great! I can help you get a quote. What would you like to move?\n\n1️⃣ Small delivery (boxes, documents)\n2️⃣ Mini-move (furniture, appliances)\n3️⃣ Business account (regular deliveries)\n\nOr click below to get a detailed quote on WhatsApp!';
        break;
      case 'areas':
        response = 'We serve:\n\n📍 Stellenbosch & surrounding areas\n📍 Somerset West & Helderberg\n📍 Cape Town CBD & Northern Suburbs\n📍 Southern Suburbs\n📍 Paarl & Winelands\n\nNeed to check a specific area? Ask me or visit our Areas page!';
        break;
      case 'pricing':
        response = 'Our pricing depends on:\n\n💰 Distance (pickup to drop-off)\n📦 Load size and weight\n⚡ Service tier (NOW/TODAY/TOMORROW)\n🏢 Stairs and lift access\n➕ Extra stops or helpers\n\nFor an accurate quote, I recommend:\n1. Use our quote form\n2. Send us photos on WhatsApp\n3. Call us directly at ' + CONTACT_INFO.phone;
        break;
      case 'whatsapp':
        setTimeout(() => {
          window.open(`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(WHATSAPP_MESSAGES.generic)}`, '_blank');
        }, 500);
        response = 'Perfect! Opening WhatsApp now... 💬\n\nYou\'ll be connected with our team for personalized assistance.';
        break;
      default:
        response = 'I\'m here to help! Ask me anything about our services.';
    }

    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      text: action === 'whatsapp' ? 'I want to talk to a human' : quickReplies.find(r => r.action === action).text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Simulate typing
    setIsTyping(true);
    setTimeout(() => {
      const botMsg = {
        id: Date.now() + 1,
        type: 'bot',
        text: response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
    }, 1000);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');

    // Generate smart response based on keywords
    setIsTyping(true);
    setTimeout(() => {
      let botResponse = generateSmartResponse(inputValue.toLowerCase());
      
      const botMsg = {
        id: Date.now() + 1,
        type: 'bot',
        text: botResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
    }, 1200);
  };

  const generateSmartResponse = (input) => {
    // Quote related
    if (input.includes('quote') || input.includes('price') || input.includes('cost') || input.includes('how much')) {
      return 'I can help you get a quote! Our pricing depends on distance, load size, and timing.\n\n🚀 Quick options:\n1. Fill our online quote form\n2. WhatsApp us with pickup/drop-off details\n3. Call ' + CONTACT_INFO.phone + '\n\nWhat would you like to move?';
    }
    
    // Service areas
    if (input.includes('area') || input.includes('location') || input.includes('stellenbosch') || input.includes('cape town')) {
      return 'We cover Stellenbosch, Cape Town, and the greater Western Cape! 📍\n\nMain areas:\n• Stellenbosch & Somerset West\n• Cape Town CBD\n• Northern & Southern Suburbs\n• Paarl & Winelands\n\nNeed a specific area checked? Let me know!';
    }

    // Service types
    if (input.includes('delivery') || input.includes('deliver')) {
      return 'We offer fast local deliveries! 📦\n\nService options:\n⚡ Rocket Van NOW - Urgent on-demand\n🌅 Rocket Van TODAY - Same-day scheduled\n📅 Rocket Van TOMORROW - Next-day (best value)\n\nAll include real-time WhatsApp updates and proof-of-delivery!\n\nReady for a quote?';
    }

    if (input.includes('move') || input.includes('furniture') || input.includes('removal')) {
      return 'Mini-moves are our specialty! 🚚\n\nWe can help with:\n✓ Single furniture pieces\n✓ Bachelor/1-bedroom apartments\n✓ Appliances and boxes\n✓ Student accommodation moves\n\nWe provide straps, blankets, and careful handling. Need helpers? We offer driver-only or +1/+2 helper options!\n\nWant a quote?';
    }

    // Business
    if (input.includes('business') || input.includes('company') || input.includes('regular')) {
      return 'Rocket Van Express Business is perfect for regular deliveries! 💼\n\nBenefits:\n✓ Monthly consolidated invoicing\n✓ Priority dispatch\n✓ 7-30 day payment terms\n✓ Dedicated account contact\n\nPerfect for e-commerce, social sellers, restaurants, and more!\n\nInterested in applying?';
    }

    // Track
    if (input.includes('track') || input.includes('where') || input.includes('status')) {
      return 'You can track your delivery easily! 📲\n\n1. Use our Track page with your booking reference (RVE-####)\n2. Check WhatsApp - we send live updates\n3. Contact us directly on WhatsApp\n\nNeed help tracking something specific?';
    }

    // Hours/availability
    if (input.includes('hour') || input.includes('open') || input.includes('available') || input.includes('time')) {
      return 'We\'re here to help! ⏰\n\n' + CONTACT_INFO.operatingHours + '\n\nFor urgent requests outside hours, WhatsApp us and we\'ll respond ASAP!';
    }

    // Contact
    if (input.includes('contact') || input.includes('phone') || input.includes('call') || input.includes('email')) {
      return 'Get in touch with us! 📞\n\nPhone/WhatsApp: ' + CONTACT_INFO.phone + '\nEmail: ' + CONTACT_INFO.email + '\n\nWe respond fastest on WhatsApp! Want me to open it for you?';
    }

    // Default helpful response
    return 'Great question! While I can help with basic info, our team can give you the best answer.\n\n💡 I suggest:\n1. Check our website pages (Services, Areas, FAQs)\n2. WhatsApp us for instant help: ' + CONTACT_INFO.phone + '\n3. Call us directly\n\nWhat else can I help with?';
  };

  return (
    <>
      {/* Chat Button - position above mobile bottom bar on mobile */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 z-[60] lg:bottom-6 lg:right-6 bg-[#fb6402] hover:bg-[#e55a02] text-white rounded-full p-4 shadow-lg transition-all hover:scale-110 animate-pulse"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 bg-[#039fff] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center pointer-events-none">
            1
          </span>
        </button>
      )}

      {/* Chat Window - position above mobile bottom bar on mobile */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-[60] lg:bottom-6 lg:right-6 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-[#039fff] rounded-full p-2">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold">Rocket Van Express</h3>
                <p className="text-xs text-gray-300">Online now</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 max-h-96">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-start space-x-2 ${
                  message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  message.type === 'bot' ? 'bg-[#039fff]' : 'bg-[#fb6402]'
                }`}>
                  {message.type === 'bot' ? (
                    <Bot className="h-4 w-4 text-white" />
                  ) : (
                    <User className="h-4 w-4 text-white" />
                  )}
                </div>
                <div
                  className={`flex-1 px-4 py-2 rounded-2xl ${
                    message.type === 'bot'
                      ? 'bg-white text-gray-800'
                      : 'bg-[#fb6402] text-white'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{message.text}</p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#039fff] flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white px-4 py-2 rounded-2xl">
                  <Loader2 className="h-4 w-4 animate-spin text-[#039fff]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 bg-white border-t flex flex-wrap gap-2">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickReply(reply.action)}
                  className="text-xs bg-gray-100 hover:bg-[#039fff]/10 text-gray-700 px-3 py-1 rounded-full transition-colors border border-gray-200 hover:border-[#039fff]"
                >
                  {reply.text}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 bg-white border-t">
            <div className="flex space-x-2">
              <Input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                className="flex-1"
              />
              <Button type="submit" size="icon" className="bg-[#fb6402] hover:bg-[#e55a02]">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
